import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, MapPin } from "lucide-react";

export function ProjectMapItEmbed() {
  const containerRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  const { orgId } = useAuth();
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;

    const loadPMIConfig = async () => {
      try {
        const { data } = await supabase
          .from("supplier_integrations")
          .select("customer_code")
          .eq("org_id", orgId)
          .eq("supplier_name", "project_mapit")
          .eq("is_active", true)
          .maybeSingle();

        if (data?.customer_code) {
          setEmbedUrl(data.customer_code);
        }
      } catch (error) {
        console.error("Error loading PMI config:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPMIConfig();
  }, [orgId]);

  useEffect(() => {
    if (!embedUrl || initialized.current) return;
    initialized.current = true;

    // Set PMI global config
    (window as any).__PMI_WIDTH__ = "100%";
    (window as any).__PMI_HEIGHT__ = "100%";
    (window as any).__PMISRC__ = embedUrl;

    // Load PMI embed script
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://projectmapit.com/static/js/embed.js";
    document.head.appendChild(script);

    return () => {
      try {
        document.head.removeChild(script);
      } catch {}
    };
  }, [embedUrl]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!embedUrl) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <MapPin className="w-10 h-10" />
        <p className="text-lg font-medium">Project Map It not configured</p>
        <p className="text-sm">Go to Settings → Integrations → Project Map It to add your embed map URL</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full" ref={containerRef}>
      <div id="__pmiembed" className="w-full h-full" />
    </div>
  );
}
